import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { missingData, type ChurchFamily, type ChurchMember } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { LegacyBadges } from '../../shared/ui/LegacyBadges';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe, useSession } from '../session/SessionProvider';
import { formatDay, fullName, roleLabel } from './roles';
import { ageFrom, formatBirthDate, genderLabel, PersonDetailsFields, readPersonDetails } from '../../shared/ui/PersonDetailsFields';
import { MemberRecordCard } from './MemberRecordCard';
import { familyRoleLabel } from './familyRoles';
import { Profile360Card } from '../profile/Profile360Card';
import { Avatar } from '../../shared/ui/Avatar';
import { PhotoPicker } from '../../shared/ui/PhotoPicker';
import { AppointmentsCard } from './AppointmentsCard';
import { showFieldErrors } from '../../shared/ui/fieldErrors';
import { whatsappTo } from '../../shared/whatsapp';
import { useTabParam } from '../../shared/ui/useTabParam';

/** Motivos rápidos de una baja; «Otro» pide escribirlo. */
const END_REASONS = ['Se mudó', 'Cambió de iglesia', 'Falleció', 'Otro'] as const;

/** Ministerios a los que pertenece o que dirige la persona. */
function MemberMinistriesCard({ churchId, personId }: { churchId: string; personId: string }) {
  const items = useAsync(
    (signal) =>
      api
        .get<{ items: { id: string; name: string; kind: string; leadership: 'leader' | 'co_leader' | null }[] }>(`/organization/churches/${churchId}/people/${personId}/ministries`, signal)
        .then((r) => r.items),
    [churchId, personId],
  );
  const hasGroup = items.status === 'ready' && items.data.some((m) => m.kind === 'grupo' && m.leadership === null);
  return (
    <Card title="Ministerios">
      {items.status === 'loading' ? <Loading /> : null}
      {items.status === 'error' ? <ErrorState error={items.error} onRetry={items.reload} /> : null}
      {items.status === 'ready' && !hasGroup ? <Alert tone="info">No tiene grupo (Varones, Damas, Jóvenes o Niños). Asígnalo desde Ministerios.</Alert> : null}
      {items.status === 'ready' && items.data.length > 0 ? (
        <ul className="ministry-tags">
          {items.data.map((m) => (
            <li key={m.id}>
              <Link to={`/app/ministerios/${m.id}`}>{m.name}</Link>
              {m.leadership === 'leader' ? <span className="badge badge--active">Líder</span> : null}
              {m.leadership === 'co_leader' ? <span className="badge badge--active">Co-líder</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

/** Familias de la iglesia a las que pertenece la persona, con sus parientes. */
function MemberFamiliesCard({ churchId, personId }: { churchId: string; personId: string }) {
  const families = useAsync((signal) => api.get<{ items: ChurchFamily[] }>(`/multichurch/churches/${churchId}/families`, signal).then((r) => r.items), [churchId]);
  const mine = families.status === 'ready' ? families.data.filter((f) => f.members.some((m) => m.personId === personId)) : [];
  return (
    <Card title="Familia">
      {families.status === 'loading' ? <Loading /> : null}
      {families.status === 'error' ? <ErrorState error={families.error} onRetry={families.reload} /> : null}
      {families.status === 'ready' && mine.length === 0 ? <p className="muted">No está en ninguna familia. Agrúpala desde Miembros → Familias.</p> : null}
      {mine.map((f) => {
        const me = f.members.find((m) => m.personId === personId)!;
        return (
          <div key={f.id} className="family-summary">
            <p>
              <strong>{f.name}</strong> · {familyRoleLabel(me.relationship)}
            </p>
            <ul className="family-summary__list">
              {f.members
                .filter((m) => m.personId !== personId)
                .map((m) => (
                  <li key={m.personId}>
                    <Link to={`/app/miembros/${m.personId}`}>{fullName(m)}</Link> <span className="muted small">· {familyRoleLabel(m.relationship)}</span>
                  </li>
                ))}
            </ul>
          </div>
        );
      })}
    </Card>
  );
}

/** Ficha de una persona de la Iglesia: datos de contacto, roles y baja. */
export function MemberPage() {
  const me = useMe();
  const { refresh } = useSession();
  const navigate = useNavigate();
  const { personId = '' } = useParams();
  const churchId = me.activeChurchId;
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const members = useAsync(
    (signal) => (churchId ? api.get<{ items: ChurchMember[] }>(`/multichurch/churches/${churchId}/members`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId],
  );
  const member = members.status === 'ready' ? members.data.find((m) => m.personId === personId) : undefined;
  const isAdmin = member?.roles.includes('admin') ?? false;
  const isMe = personId === me.personId;
  const canEnd = me.can('members.manage') && Boolean(member?.membershipId) && !isMe;
  // «Administración»: permisos (rol de Administrador) y dar de baja.
  const showAdmin = me.can('roles.assign') || canEnd;
  const [editing, setEditing] = useState(false);
  // La ficha en cuatro pestañas; la elegida vive en la dirección (?ver=…).
  const [tab, setTab] = useTabParam(['datos', 'iglesia', 'expediente', 'administracion'] as const, 'ver');
  // «Dar de baja» abre aquí mismo el motivo con opciones rápidas.
  const [ending, setEnding] = useState(false);
  const [endReason, setEndReason] = useState<(typeof END_REASONS)[number] | null>(null);
  const [endOther, setEndOther] = useState('');
  const view = tab === 'administracion' && !showAdmin ? 'datos' : tab;

  async function saveDetails(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Quien administra puede guardar aunque falten datos que la persona no ha dado (solo nombre y apellido).
    const formEl = event.currentTarget;
    const { details, error, fields } = readPersonDetails(new FormData(formEl), { requireAll: false });
    if (error) {
      setFeedback({ tone: 'error', text: error });
      showFieldErrors(formEl, fields);
      return;
    }
    await run(() => api.put(`/identity/people/${personId}/details`, details), 'Datos guardados.');
    setEditing(false);
  }

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setFeedback(null);
    try {
      await action();
      setFeedback({ tone: 'success', text: success });
      members.reload();
      if (isMe) await refresh();
    } catch (e) {
      setFeedback({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  function toggleAdmin() {
    if (!member) return;
    if (isAdmin) {
      const warning = isMe ? '¿Quitarte a ti el rol de Administrador? Perderás el acceso a esta administración.' : `¿Quitar a ${fullName(member)} el rol de Administrador?`;
      if (!window.confirm(warning)) return;
      void run(
        () => api.post(`/security/churches/${churchId}/role-assignments/revoke`, { personId, roleKey: 'admin' }),
        `${fullName(member)} ya no es administrador.`,
      );
    } else {
      if (!window.confirm(`¿Hacer a ${fullName(member)} Administrador? Podrá ver y cambiar todo en la iglesia.`)) return;
      void run(() => api.post(`/security/churches/${churchId}/role-assignments`, { personId, roleKey: 'admin' }), `${fullName(member)} ahora es administrador.`);
    }
  }

  async function endMembership() {
    if (!member?.membershipId) return;
    const reason = endReason === 'Otro' ? endOther.trim() : endReason;
    if (!reason) {
      setFeedback({ tone: 'error', text: 'Elige el motivo de la baja.' });
      return;
    }
    if (reason.length < 3) {
      setFeedback({ tone: 'error', text: 'Escribe el motivo de la baja.' });
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      await api.post(`/multichurch/churches/${churchId}/memberships/${member.membershipId}/end`, { reason });
      navigate('/app/miembros', { replace: true });
    } catch (e) {
      setFeedback({ tone: 'error', text: errorMessage(e) });
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <p>
        <Link to="/app/miembros">← Miembros</Link>
        {member ? (
          <>
            {' · '}
            <Link to={`/app/personas/${member.personId}`}>Ver perfil</Link>
          </>
        ) : null}
      </p>
      {members.status === 'loading' ? <Loading /> : null}
      {members.status === 'error' ? <ErrorState error={members.error} onRetry={members.reload} /> : null}
      {members.status === 'ready' && !member ? <EmptyState title="Esta persona ya no está en el directorio de la iglesia." /> : null}
      {member ? (
        <>
          <header className="page__header">
            <h1>
              {fullName(member)}
              <LegacyBadges founder={member.founder} />
            </h1>
            <p className="muted">{member.roles.map(roleLabel).join(' · ') || 'Sin rol'}</p>
          </header>
          {me.can('members.manage') ? <PhotoPicker personId={member.personId} name={fullName(member)} self={member.personId === me.personId} /> : <Avatar personId={member.personId} name={fullName(member)} size={72} />}
          {feedback ? <Alert tone={feedback.tone}>{feedback.text}</Alert> : null}
          <div className="tabs tabs--scroll" role="tablist" aria-label="Ficha del miembro">
            {(
              [
                ['datos', 'Datos'],
                ['iglesia', 'Iglesia'],
                ['expediente', 'Expediente'],
                ...(showAdmin ? ([['administracion', 'Administración']] as const) : []),
              ] as const
            ).map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={view === key} className={`tabs__tab${view === key ? ' tabs__tab--active' : ''}`} onClick={() => setTab(key)}>
                {label}
              </button>
            ))}
          </div>
          {view === 'datos' ? (
          <Card title="Datos">
            {editing ? (
              <form className="form" onSubmit={saveDetails} noValidate>
                <PersonDetailsFields person={member} idPrefix="md" />
                <div className="actions">
                  <Button type="submit" busy={busy}>
                    Guardar
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                    Cancelar
                  </Button>
                </div>
              </form>
            ) : null}
            {!editing && (member.hasAccount === false || missingData(member).length > 0) ? (
              <Alert tone="info">
                {member.hasAccount === false ? 'Capturado por la iglesia: todavía no tiene cuenta en KEORA. Si se registra con su mismo nombre, en «Por aprobar» podrás unir su cuenta con esta ficha y conserva todo (promesas, familia, historial). ' : ''}
                {missingData(member).length ? `Le falta: ${missingData(member).join(', ')}. Complétalo con «Editar datos».` : ''}
              </Alert>
            ) : null}
            {editing ? null : (
            <dl className="details">
              <dt>Nombre(s)</dt>
              <dd>{member.givenName}</dd>
              <dt>Apellido paterno</dt>
              <dd>{member.paternalSurname || '—'}</dd>
              <dt>Apellido materno</dt>
              <dd>{member.maternalSurname || '—'}</dd>
              <dt>Género</dt>
              <dd>{member.gender ? genderLabel(member.gender) : <span className="muted">Sin registrar</span>}</dd>
              <dt>Celular</dt>
              <dd>
                {member.mobilePhone ? (
                  <>
                    <a href={`tel:${member.mobilePhone.replace(/[^\d+]/g, '')}`}>{member.mobilePhone}</a> ·{' '}
                    <a href={whatsappTo(member.mobilePhone)} target="_blank" rel="noopener noreferrer">
                      WhatsApp
                    </a>
                  </>
                ) : (
                  <span className="muted">Sin registrar</span>
                )}
              </dd>
              <dt>Teléfono de casa</dt>
              <dd>{member.homePhone ? <a href={`tel:${member.homePhone.replace(/[^\d+]/g, '')}`}>{member.homePhone}</a> : <span className="muted">Sin registrar</span>}</dd>
              <dt>Nacimiento</dt>
              <dd>
                {member.birthDate ? `${formatBirthDate(member.birthDate)} (${ageFrom(member.birthDate)} años)` : <span className="muted">Sin registrar</span>}
              </dd>
              <dt>Lugar de nacimiento</dt>
              <dd>{member.birthPlace ?? <span className="muted">Sin registrar</span>}</dd>
              <dt>Dirección</dt>
              <dd>{member.address ?? <span className="muted">Sin registrar</span>}</dd>
              <dt>Correo</dt>
              <dd>{member.email ? <a href={`mailto:${member.email}`}>{member.email}</a> : '—'}</dd>
              <dt>Miembro desde</dt>
              <dd>{member.membershipId ? formatDay(member.memberSince) : 'No tiene membresía (solo un rol en la iglesia)'}</dd>
            </dl>
            )}
            {!editing && me.can('members.manage') ? (
              <div className="actions">
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  Editar datos
                </Button>
              </div>
            ) : null}
            {!editing && me.can('members.manage') && member.hasAccount === false && churchId ? (
              <form
                className="form form--inline"
                onSubmit={(e) => {
                  e.preventDefault();
                  const email = String(new FormData(e.currentTarget).get('accessEmail') ?? '').trim();
                  void run(() => api.post(`/multichurch/churches/${churchId}/members/${member.personId}/access`, { email }), `Le enviamos a ${email.toLowerCase()} el correo para crear su contraseña. Entrará con esta misma ficha.`);
                }}
              >
                <Field label="Darle acceso a KEORA con su correo" name="accessEmail" type="email" required autoComplete="off" hint="Se crea su cuenta sobre esta ficha: conserva sus promesas, familia e historial." />
                <Button type="submit" variant="secondary" busy={busy}>Enviar invitación</Button>
              </form>
            ) : null}
          </Card>
          ) : null}
          {view === 'iglesia' && churchId ? (
            <>
              <MemberMinistriesCard churchId={churchId} personId={member.personId} />
              <MemberFamiliesCard churchId={churchId} personId={member.personId} />
              <AppointmentsCard churchId={churchId} personId={member.personId} canEdit={me.can('roles.assign')} />
            </>
          ) : null}
          {view === 'expediente' && churchId ? (
            <>
              <MemberRecordCard churchId={churchId} personId={member.personId} canEdit={me.can('members.manage')} baptized={member.baptized} />
              {me.can('members.manage') ? <Profile360Card churchId={churchId} personId={member.personId} /> : null}
            </>
          ) : null}
          {view === 'administracion' && showAdmin && me.can('roles.assign') ? (
            <Card title="Administración">
              <p className="muted">
                {isAdmin
                  ? 'Es Administrador: puede ver y cambiar todo en la iglesia, aprobar miembros y asignar roles.'
                  : 'Un Administrador puede ver y cambiar todo en la iglesia, aprobar miembros y asignar roles.'}
              </p>
              <div className="actions">
                <Button variant={isAdmin ? 'secondary' : 'primary'} onClick={toggleAdmin} busy={busy}>
                  {isAdmin ? 'Quitar rol de Administrador' : 'Hacer Administrador'}
                </Button>
              </div>
            </Card>
          ) : null}
          {view === 'administracion' && canEnd ? (
            <Card title="Dar de baja">
              <p className="muted">Termina su membresía en esta iglesia. Su historial se conserva y puede volver a solicitar su ingreso.</p>
              {ending ? (
                <div className="reject-box">
                  <fieldset className="field">
                    <legend>Motivo (queda en su historial)</legend>
                    <div className="actions actions--wrap">
                      {END_REASONS.map((r) => (
                        <label key={r} className="check">
                          <input type="radio" name="end-reason" value={r} checked={endReason === r} onChange={() => setEndReason(r)} /> {r}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  {endReason === 'Otro' ? (
                    <div className="field">
                      <label htmlFor="end-other">¿Cuál es el motivo?</label>
                      <textarea id="end-other" rows={2} maxLength={500} value={endOther} onChange={(e) => setEndOther(e.target.value)} />
                    </div>
                  ) : null}
                  <div className="actions">
                    <Button variant="danger" onClick={endMembership} busy={busy} disabled={!endReason}>
                      Confirmar baja
                    </Button>
                    <Button variant="ghost" onClick={() => setEnding(false)}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="actions">
                  <Button variant="danger" onClick={() => setEnding(true)}>
                    Dar de baja
                  </Button>
                </div>
              )}
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
